import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { getApiConfig, resolveBaseUrl } from "./config";

const rawQuery = fetchBaseQuery({
  baseUrl: "/",
  // Force each request through the network so the browser never serves a
  // stale error response (e.g. a cached 410 from an expired-token request).
  fetchFn: (input, init) => fetch(input, { ...init, cache: "no-store" }),
  prepareHeaders: (headers) => {
    const jwtKey = getApiConfig().jwtKey;
    const jwt = jwtKey ? (localStorage.getItem(jwtKey) ?? "") : "";
    if (jwt) headers.set("x-pulse-jwt", jwt);
    return headers;
  },
  // Empty response bodies (e.g. 200/204 with no content) would cause
  // fetchBaseQuery's default JSON parser to throw, surfacing as a spurious
  // error. Parse as text first and only JSON-decode when there is content.
  responseHandler: async (response) => {
    const text = await response.text();
    if (!text.trim()) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  },
});

/**
 * Custom base query that resolves URLs at request time.
 *
 * URL resolution order:
 * 1. If `args.url` starts with "http" — use as absolute URL, no prefix.
 * 2. If `args.baseUrl` is set — prepend it to `args.url`.
 * 3. Otherwise — prepend the app-level configured base URL.
 */
const baseQueryWithDynamicUrl: BaseQueryFn<
  string | (FetchArgs & { baseUrl?: string }),
  unknown,
  FetchBaseQueryError
> = async (args, api, extra) => {
  const req: FetchArgs & { baseUrl?: string } =
    typeof args === "string" ? { url: args } : args;

  const base = req.baseUrl ?? resolveBaseUrl();
  const finalUrl = req.url.startsWith("http") ? req.url : `${base}${req.url}`;

  // Strip the custom `baseUrl` field before passing to fetchBaseQuery
  const { baseUrl: _, ...fetchArgs } = req;
  const finalArgs: FetchArgs = { ...fetchArgs, url: finalUrl };

  const result = await rawQuery(finalArgs, api, extra);

  if (result.error) {
    const status = result.error.status;
    if (status === 401 || status === 403 || status === 410) {
      getApiConfig().onAuthError?.(status as number);
    }
    if (status === 401 || status === 410) {
      getApiConfig().onSessionExpiry?.();
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: "pulseWidgetApi",
  baseQuery: baseQueryWithDynamicUrl,
  tagTypes: [
    "UserPortfolios",
    "OrgPortfolios",
    "OrgMetadata",
    "Accounts",
    "PositionSummary",
    "Overview",
    "BlotterHistory",
    "AlertChecks",
    "AlertDestinations",
    "AlertGroupDestinations",
    "UserRoles",
    "OrgUsers",
    "Strategies",
    "AccountStrategy",
    "ApiKeys",
    "Entities",
    "StaticPriceOverrides",
    "ManualAdjustments",
    "MarkupTiers",
    "AccountTiers",
    "RefdataAssets",
    "RefdataInstruments",
    "AlgoStatus",
    "AlgoInteractions",
    "AccountManagerAccounts",
    "HouseAccounts",
    "ClstAccounts",
    "CustomerConfigurations",
    "MarketAccounts",
    "Trades",
    "HarukoLoans",
  ],
  endpoints: () => ({}),
});
